import { ITileData } from '../../../network/network-top-level/net-dash-tile-data/net-dash-tile-data.service';
import { Injectable } from '@angular/core';
import { compact, find, isNil, reduce } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { INetworkTile } from 'Src/ng2/network/network-top-level/tiles/interfaces/dashboard.interfaces';
import { IColumnStuLvl, IRowData } from '../../models/list-models';

export type TSortDirections = 'asc' | 'desc';
export interface IStudentDetails {
  studentName: string;
  studentId: string;
}

type TColumnData = string | number;
export enum Direction {
  asc = 'asc',
  desc = 'desc',
}

enum DataType {
  number = 'number',
  string = 'string',
}

const statusOrderMap = {
  'Needs confirmation': 4,
  Complete: 3,
  Incomplete: 2,
  'In progress': 1,
  Upcoming: 0,
};

/* istanbul ignore next */
@Injectable()
export class SortAndFilterService {
  static updateSortCol(
    colKey: string,
    sortKey$: BehaviorSubject<string>,
    sortDirection$: BehaviorSubject<TSortDirections>,
  ) {
    const shouldToggleSort = colKey === sortKey$.value;
    if (shouldToggleSort) sortDirection$.next(SortAndFilterService.getToggledSort(sortDirection$.value));
    else sortDirection$.next('desc');
    sortKey$.next(colKey);
  }

  static sortRollupTableRowData(rowData: IRowData[][], sortKeyIndex: number, sortDirection: TSortDirections) {
    return [...rowData].sort((a, b) => {
      let aSortValue: string | number;
      let bSortValue: string | number;

      if (sortKeyIndex === 0) {
        aSortValue = a[sortKeyIndex].meta.data;
        bSortValue = b[sortKeyIndex].meta.data;
      } else {
        aSortValue = a[sortKeyIndex].data;
        bSortValue = b[sortKeyIndex].data;
      }

      if (aSortValue > bSortValue) return sortDirection === 'asc' ? 1 : -1;
      if (aSortValue < bSortValue) return sortDirection === 'asc' ? -1 : 1;
      return 0;
    });
  }

  static sortTableRowData(rowData: IRowData[], sortKeyIndex: number, sortDirection: TSortDirections, colKey?: string) {
    return [...rowData].sort((a, b) => {
      if (!isNil(sortKeyIndex)) {
        const [parsedAVal, parsedBVal] = SortAndFilterService.getSortValues(
          a[sortKeyIndex].data,
          b[sortKeyIndex].data,
          colKey,
        );
        return SortAndFilterService._sortValues(parsedAVal, parsedBVal, sortDirection);
      } else {
        let aData;
        let bData;
        if (a.meta && a.meta.isoDate) {
          aData = a.meta.isoDate;
          bData = b.meta.isoDate;
        } else {
          aData = a.data;
          bData = b.data;
        }
        return SortAndFilterService._sortValues(aData, bData, sortDirection);
      }
    });
  }

  static getSortValues(a, b, key?: string): string[] {
    let aSortValue;
    let bSortValue;
    switch (key) {
      case 'STUDENT_ACTIVITY_STATUS':
        aSortValue = `${statusOrderMap[a]}`;
        bSortValue = `${statusOrderMap[b]}`;
        break;
      case 'STUDENT_ACTIVITY_HOURS':
        const aVals = a ? a.split(' of ') : a;
        const bVals = b ? b.split(' of ') : b;
        aSortValue = aVals ? (aVals[0] === '-' ? '-1' : aVals[0]) : '-1';
        bSortValue = bVals ? (bVals[0] === '-' ? '-1' : bVals[0]) : '-1';
        break;
      case 'CURRENT_REMOTE_INTERACTION_STREAK':
        const getStatus = streak => {
          if (streak === null) return -1;
          const [num] = streak.split(' ');
          return parseInt(num);
        };
        aSortValue = getStatus(a);
        bSortValue = getStatus(b);
        break;
      default:
        aSortValue = a;
        bSortValue = b;
    }
    return [aSortValue, bSortValue];
  }

  static studentDetailsParser(rows: IRowData[]) {
    let studentDetails: IStudentDetails;
    const [nameRow] = rows;
    const { meta } = nameRow;
    if (!meta) {
      studentDetails = reduce(
        rows,
        (reducedRows, studentDetails) => {
          if (studentDetails.columnKey === 'Student Name') reducedRows.studentName = studentDetails.data;
          if (studentDetails.columnKey === 'Student Id') reducedRows.studentId = studentDetails.data;
          return reducedRows;
        },
        { studentName: null, studentId: null },
      );
    } else {
      const filterCol = rows[0];
      const { data: studentName, meta } = filterCol;
      const studentId = JSON.parse(meta).data;
      studentDetails = { studentName, studentId };
    }
    return studentDetails;
  }

  static filterRows(args: { filterTerm: string; listType: string; rowData: IRowData[][] }) {
    const { filterTerm, listType, rowData } = args;
    let filteredRows;
    if (!filterTerm || !filterTerm.trim() || rowData.length === 0) {
      filteredRows = rowData;
    } else if (listType === 'USER') {
      filteredRows = SortAndFilterService.filterStubColumn({ filterTerm, rowData });
    } else if (listType === 'STUDENT') {
      filteredRows = SortAndFilterService.filterStudentRows({ filterTerm, rowData });
    } else if (listType === 'NETWORK') {
      filteredRows = SortAndFilterService.filterStubColumn({ filterTerm, rowData });
    } else if (listType === 'USER_SCHOOL_PORTFOLIO') {
      filteredRows = SortAndFilterService.filterSchoolRows({ filterTerm, rowData });
    } else {
      filteredRows = SortAndFilterService.filterStudentRows({ filterTerm, rowData });
    }
    return filteredRows;
  }

  static filterStudentRows(args: { filterTerm: string; rowData: IRowData[][] }) {
    const { filterTerm, rowData } = args;
    const allFilterTerms = compact(filterTerm.split(' '));

    return [...rowData].filter((rows: IRowData[]) => {
      const studentDetails: IStudentDetails = SortAndFilterService.studentDetailsParser(rows);

      return allFilterTerms.some(filterTerm => {
        const nameMatch = studentDetails.studentName
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());

        const studentIdMatch = studentDetails.studentId
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());

        if (nameMatch || studentIdMatch) return true;
      });
    });
  }

  static filterStubColumn(args: { filterTerm: string; rowData: IRowData[][] }) {
    const { filterTerm, rowData } = args;
    let allFilterTerms;

    if (filterTerm.includes(',')) {
      allFilterTerms = filterTerm.split(',').map(v => v.trim());
    } else {
      allFilterTerms = filterTerm.split(' ');
    }
    allFilterTerms = allFilterTerms.filter(term => term !== '');

    return [...rowData].filter((rows: IRowData[]) => {
      const [{ data }] = rows;
      return allFilterTerms.some(filterTerm => {
        return data
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());
      });
    });
  }

  static filterSchoolRows(args: { filterTerm: string; rowData: IRowData[][] }) {
    const { filterTerm, rowData } = args;
    let allFilterTerms;

    if (filterTerm.includes(',')) {
      allFilterTerms = filterTerm.split(',').map(v => v.trim());
    } else {
      allFilterTerms = filterTerm.split(' ');
    }
    allFilterTerms = allFilterTerms.filter(term => term !== '');

    return [...rowData].filter((rows: IRowData[]) => {
      const col = find(rows, { columnKey: 'STUB' });
      const meta = JSON.parse(col.meta);
      const schoolName = col.data;
      const schoolId = meta.data;
      const schoolNickname = meta.nickname;
      return allFilterTerms.some(filterTerm => {
        const nameMatch = schoolName
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());

        const idMatch = schoolId
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());

        const nicknameMatch = schoolNickname
          .toString()
          .toLowerCase()
          .includes(filterTerm.toString().toLowerCase());

        if (nameMatch || idMatch || nicknameMatch) return true;
      });
    });
  }

  static _sortValues(aVal: string | number, bVal: string | number, sortDirection: TSortDirections): -1 | 0 | 1 {
    // If one of the two values is null,
    // it doesn't matter the type we are sorting
    // just return the proper num. `sortDirection` supports toggling
    if (isNil(aVal) && sortDirection === Direction.asc) return -1;
    if (isNil(aVal) && sortDirection === Direction.desc) return 1;
    if (isNil(bVal) && sortDirection === Direction.asc) return 1;
    if (isNil(bVal) && sortDirection === Direction.desc) return -1;
    if (isNil(aVal) && isNil(bVal)) return 0;
    // Determine which type to cast to
    const dataType = SortAndFilterService._isNumeric(aVal) ? DataType.number : DataType.string;
    // Targets
    let sortValueA: number | string;
    let sortValueB: number | string;
    // Cast values
    if (dataType === DataType.number) {
      sortValueA = +aVal;
      sortValueB = +bVal;
    } else {
      sortValueA = aVal;
      sortValueB = bVal;
    }
    // Make the comparision
    if (sortValueA > sortValueB) return sortDirection === 'asc' ? 1 : -1;
    if (sortValueA < sortValueB) return sortDirection === 'asc' ? -1 : 1;
    // Casted values are equal
    return 0;
  }

  static _isNumeric(val) {
    return !isNaN(val);
  }

  static getToggledSort(sort: TSortDirections): TSortDirections {
    return sort === Direction.asc ? Direction.desc : Direction.asc;
  }

  static filterTileData({ filterTerm, tileData }): Array<ITileData> {
    const allFilterTerms = filterTerm.split(',');

    if (!filterTerm || filterTerm.trim() === '') return tileData;

    return tileData.filter(tile => {
      return allFilterTerms.some(filterTerm => {
        const categoryMatch = tile.category
          .toString()
          .toLowerCase()
          .includes(
            filterTerm
              .toString()
              .trim()
              .toLowerCase(),
          );

        const tileNameMatch = tile.tile
          .toString()
          .toLowerCase()
          .includes(
            filterTerm
              .toString()
              .trim()
              .toLowerCase(),
          );

        return categoryMatch || tileNameMatch;
      });
    });
  }
}
